import { useState } from "react";
import { useTabDraft } from "@/hooks/useTabDraft";
import { ToolContainer } from "@/components/ToolLayout";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { Checkbox } from "@/components/ui/checkbox";
import { encodeEntities, decodeEntities, DEFAULT_ENCODE, type EncodeOptions } from "./htmlEntities";
import { copyText } from "@/lib/copyText";

const TOOL_INFO_CONTENT = {
  what: `Encodes special characters into HTML entities (like converting a literal < into &lt;) or decodes HTML entities back into their original characters.`,
  how: `Paste your text or HTML, choose encode or decode, and the converted result appears immediately.`,
  tip: `This matters most for characters that have special meaning in HTML itself, like angle brackets and ampersands — encoding these correctly prevents them from being misinterpreted as actual HTML markup.`,
};

export default function HtmlEntityConverter() {
  const [input, setInput] = useTabDraft(
    "html-entity-converter",
    '<div class="example">Hello & welcome!</div>'
  );
  const [mode, setMode] = useState<"encode" | "decode">("encode");

  const [opts, setOpts] = useState<EncodeOptions>(DEFAULT_ENCODE);
  const output =
    mode === "encode" ? encodeEntities(input, opts) : decodeEntities(input);

  const copy = () => {
    void copyText(output, "Copied to clipboard");
  };

  const swap = () => {
    setMode(mode === "encode" ? "decode" : "encode");
    setInput(output);
  };

  const { blocked } = useLogToolUsage(true, 3000, { ownActions: true });


  return (
    <ToolContainer>
      <div className="flex items-center gap-3 mb-4">
        <Button
          size="sm"
          variant={mode === "encode" ? "default" : "outline"}
          onClick={() => setMode("encode")}
        >
          Encode
        </Button>
        <Button
          size="sm"
          variant={mode === "decode" ? "default" : "outline"}
          onClick={() => setMode("decode")}
        >
          Decode
        </Button>
        <Button size="sm" variant="ghost" onClick={swap}>
          ⇅ Swap
        </Button>
      </div>
      {mode === "encode" && (
        <div className="flex flex-wrap gap-x-6 gap-y-2 mb-4 text-sm">
          {([
            ["quotes", "Encode quotes (&quot; &#39;)"],
            ["named", "Named entities (&copy; instead of &#169;)"],
            ["nonAsciiOnly", "Non-ASCII characters only"],
          ] as const).map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={opts[k]}
                onCheckedChange={v => setOpts({ ...opts, [k]: v === true })}
              />
              {label}
            </label>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="mb-2 block">
            {mode === "encode" ? "Raw text / HTML" : "Encoded (entities)"}
          </Label>
          <Textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            className="min-h-[200px] font-mono text-sm"
          />
        </div>
        <div>
          <Label className="mb-2 block">
            {mode === "encode" ? "Encoded (entities)" : "Decoded"}
          </Label>
          <Textarea
            readOnly
            value={blocked ? "— blocked at today's usage limit —" : output}
            className="min-h-[200px] font-mono text-sm bg-muted/30"
          />
        </div>
      </div>
      <Button className="mt-4" onClick={copy} disabled={blocked}>
        Copy Output
      </Button>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
